import {
  getUserAvailabilityActivity,
  type UserAvailabilityActivity,
} from '@api/queries/getUserAvailabilityActivity';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import {
  AgentStatusAccuracy,
  RTCSMetricNames,
  RTCSMetricTags,
} from '../../metrics/rtcsMetrics';
import { logException } from './log';
import { serviceState } from './state';

/**
 * Compares the locally cached availability/activity status of a target agent
 * against a fresh single-user fetch and emits an accuracy metric. Pure
 * monitoring — the BFF response is NOT written back to serviceState because
 * even a small UI flicker at transfer/call initiation is undesirable. Fetch
 * errors are logged (so a schema or BFF break is observable) but not
 * rethrown, so verification never disrupts the call flow.
 */
export async function verifyAgentStatus(userId: string): Promise<void> {
  if (
    !FeatureFlagModule.isEnabled(FeatureFlagKeys.featureCheckCalleeStatusOnCall)
  ) {
    return;
  }
  if (!userId) {
    return;
  }

  const cached = serviceState.userById[userId];
  if (!cached) {
    return;
  }
  const cachedAvailability = cached.availabilityStatus;
  const cachedActivity = cached.activityStatus;

  let fresh: UserAvailabilityActivity | null;
  try {
    fresh = await getUserAvailabilityActivity(userId);
  } catch (err) {
    logException(err, 'verifyAgentStatus:fetch-failed');
    return;
  }
  if (!fresh) {
    return;
  }

  const isMatch =
    fresh.availabilityStatus === cachedAvailability &&
    fresh.activityStatus === cachedActivity;

  DiagnosticsModule.incrementCounter(
    RTCSMetricNames.RTCS_AGENT_STATUS_ACCURACY_TOTAL,
    {
      [RTCSMetricTags.AGENT_STATUS_ACCURACY]: isMatch
        ? AgentStatusAccuracy.MATCH
        : AgentStatusAccuracy.MISMATCH,
    },
  );
}
